import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPOS = [
  { v: "activo", l: "Activo" },
  { v: "pasivo", l: "Pasivo" },
  { v: "patrimonio", l: "Patrimonio Neto" },
  { v: "ingreso", l: "Ingreso" },
  { v: "egreso", l: "Egreso" },
];

const tipoColor = {
  activo: "text-emerald-700 bg-emerald-50",
  pasivo: "text-red-700 bg-red-50",
  patrimonio: "text-indigo-700 bg-indigo-50",
  ingreso: "text-blue-700 bg-blue-50",
  egreso: "text-amber-700 bg-amber-50",
};

const EMPTY_FORM = { codigo: "", nombre: "", tipo: "activo", parent_codigo: "__none__", imputable: true };

export default function PlanCuentasPage() {
  const [cuentas, setCuentas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // cuenta id | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [q, setQ] = useState("");
  const [delTarget, setDelTarget] = useState(null); // cuenta a eliminar

  const load = async () => {
    const { data } = await api.get("/cuentas");
    setCuentas(data);
  };
  useEffect(() => { load(); }, []);

  const parentOptions = useMemo(
    () => cuentas.filter((c) => !c.imputable && (!editing || c.id !== editing)),
    [cuentas, editing]
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return cuentas;
    const s = q.toLowerCase();
    return cuentas.filter(
      (c) => c.codigo.toLowerCase().includes(s) || c.nombre.toLowerCase().includes(s)
    );
  }, [cuentas, q]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      codigo: c.codigo,
      nombre: c.nombre,
      tipo: c.tipo,
      parent_codigo: c.parent_codigo || "__none__",
      imputable: c.imputable,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      tipo: form.tipo,
      parent_codigo: form.parent_codigo === "__none__" ? null : form.parent_codigo,
      imputable: form.imputable,
    };
    try {
      if (editing) {
        await api.put(`/cuentas/${editing}`, payload);
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/cuentas", payload);
        toast.success("Cuenta creada");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/cuentas/${delTarget.id}`);
      toast.success("Cuenta eliminada");
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const depth = (c) => Math.max(0, (c.codigo.match(/\./g) || []).length);

  return (
    <>
      <Topbar
        title="Plan de Cuentas"
        subtitle="Estructura jerárquica del árbol contable. Editable y eliminable si no tiene movimientos."
        right={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999]" />
              <Input
                data-testid="cuentas-search"
                placeholder="Buscar código o nombre…"
                className="rounded-sm h-9 w-64 pl-8 text-[13px]"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Button
              data-testid="new-cuenta-btn"
              onClick={openNew}
              className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9"
            >
              <Plus size={16} className="mr-1" /> Nueva cuenta
            </Button>
          </>
        }
      />

      <div className="p-4">
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-2 bg-neutral-50 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
              {filtered.length} de {cuentas.length} cuentas
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-5 py-2 text-left w-40">Código</th>
                  <th className="px-5 py-2 text-left">Nombre</th>
                  <th className="px-5 py-2 text-left w-32">Tipo</th>
                  <th className="px-5 py-2 text-left w-28">Clase</th>
                  <th className="px-5 py-2 text-right w-32">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`cuenta-${c.codigo}`}
                    className="hover:bg-neutral-50 group"
                  >
                    <td className="px-5 py-2 font-mono">{c.codigo}</td>
                    <td
                      className="px-5 py-2"
                      style={{ paddingLeft: `${20 + depth(c) * 16}px` }}
                    >
                      <span className={!c.imputable ? "font-semibold" : ""}>{c.nombre}</span>
                    </td>
                    <td className="px-5 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-sm text-[11px] capitalize font-mono ${tipoColor[c.tipo]}`}
                      >
                        {c.tipo}
                      </span>
                    </td>
                    <td className="px-5 py-2 text-[11px] font-mono">
                      {c.imputable ? (
                        <span className="text-[#666]">Imputable</span>
                      ) : (
                        <span className="text-[#6B1F24] font-semibold">Cuenta madre</span>
                      )}
                    </td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          data-testid={`edit-cuenta-${c.codigo}`}
                          onClick={() => openEdit(c)}
                          className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                          title="Editar"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          data-testid={`del-cuenta-${c.codigo}`}
                          onClick={() => setDelTarget(c)}
                          className="p-1.5 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                          title="Eliminar"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-[#999]">
                      Sin resultados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Sheet crear / editar */}
      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setEditing(null);
            setForm(EMPTY_FORM);
          }
        }}
      >
        <SheetContent className="w-full sm:w-[460px] sm:max-w-[460px]">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {editing ? "Editar cuenta" : "Nueva cuenta contable"}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={save} className="space-y-4 mt-6">
            <div>
              <Label className="text-[12px]">Código</Label>
              <Input
                data-testid="cuenta-codigo"
                className="rounded-sm mt-1 font-mono"
                placeholder="ej: 1.1.1.5"
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                required
              />
              {editing && (
                <div className="text-[11px] text-amber-700 mt-1">
                  Al cambiar el código, todos los movimientos y asientos vinculados se actualizarán.
                </div>
              )}
            </div>
            <div>
              <Label className="text-[12px]">Nombre</Label>
              <Input
                data-testid="cuenta-nombre"
                className="rounded-sm mt-1"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-[12px]">Tipo</Label>
              <Select
                value={form.tipo}
                onValueChange={(v) => setForm({ ...form, tipo: v })}
              >
                <SelectTrigger data-testid="cuenta-tipo" className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t.v} value={t.v}>
                      {t.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px]">Cuenta padre</Label>
              <Select
                value={form.parent_codigo}
                onValueChange={(v) => setForm({ ...form, parent_codigo: v })}
              >
                <SelectTrigger data-testid="cuenta-parent" className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__none__">
                    <span className="text-[#6B1F24] font-semibold">— Sin padre (cuenta raíz) —</span>
                  </SelectItem>
                  {parentOptions.map((p) => (
                    <SelectItem key={p.codigo} value={p.codigo}>
                      <span className="font-mono">{p.codigo}</span> · {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-[#666] mt-1">
                Solo cuentas madre pueden ser padre. Elegí "Sin padre" para crear una cuenta de nivel superior.
              </div>
            </div>
            <div className="flex items-center justify-between border border-[#E5E5E5] rounded-sm p-3">
              <div>
                <div className="text-[13px] font-medium">Cuenta imputable</div>
                <div className="text-[11px] text-[#666]">
                  {form.imputable
                    ? "Se pueden registrar movimientos directamente."
                    : "Cuenta madre / de agrupación (no acepta movimientos)."}
                </div>
              </div>
              <Switch
                data-testid="cuenta-imputable"
                checked={form.imputable}
                onCheckedChange={(v) => setForm({ ...form, imputable: v })}
              />
            </div>
            <Button
              data-testid="save-cuenta-btn"
              type="submit"
              className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
            >
              {editing ? "Guardar cambios" : "Crear cuenta"}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Confirm delete */}
      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar cuenta</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar la cuenta{" "}
              <span className="font-mono">{delTarget?.codigo}</span> · {delTarget?.nombre}?
              <br />
              Solo se puede eliminar si no tiene movimientos ni subcuentas asociadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-cuenta"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
